import Service, { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import BookingModel from 'uplisting-frontend/models/booking';
import { OccasionRecord } from 'uplisting-frontend/services/calendar/-internals/record';
import { formatDateQuery } from 'uplisting-frontend/utils';

interface IDragOperation {
  record: OccasionRecord;
  targetDate?: Date;
  isValid: boolean;
}

export default class CalendarInteractionService extends Service {
  @service router!: Services['router'];
  @service notifications!: Services['notifications'];
  @service('calendar/data') calendarData!: Services['calendar/data'];

  @cached @tracked dragOperation: IDragOperation | null = null;

  @cached
  get isDragInvalid(): boolean {
    const { dragOperation: operation } = this;

    return !operation?.isValid || !operation.targetDate;
  }

  @action
  startDrag(record: OccasionRecord): void {
    if (!record.meta.isDraggable) {
      return;
    }

    this.dragOperation = {
      record,
      isValid: false,
    };
  }

  @action
  updateDrag(targetDate: Date): void {
    if (!this.dragOperation) {
      return;
    }

    this.dragOperation.targetDate = targetDate;

    this.dragOperation.isValid = this.dragOperation.record.meta.isDraggable;
  }

  @action
  @waitFor
  async completeDrag(): Promise<void> {
    if (this.isDragInvalid) {
      this.#resetDragOperation();

      return;
    }

    try {
      await this.#performOccasionMove();

      this.notifications.info('calendar.notifications.moved_successfully');
    } catch {
      this.notifications.error('calendar.notifications.moved_failure');
    } finally {
      this.#resetDragOperation();
    }
  }

  #resetDragOperation(): void {
    this.dragOperation = null;
  }

  @action
  @waitFor
  async handleOccasionClick(record: OccasionRecord): Promise<void> {
    if (!record.meta.isClickable) {
      return;
    }

    if (record.model instanceof BookingModel) {
      await this.router.transitionTo(
        'xcalendar.bookings.summary',
        record.model.id,
      );
    }
  }

  async #performOccasionMove(): Promise<void> {
    const operation = this.dragOperation;

    if (!operation || this.isDragInvalid) {
      return;
    }

    const { record, targetDate } = operation;

    const { duration } = record.meta;

    if (record.model instanceof BookingModel) {
      const newCheckOut = new Date(targetDate as Date);
      newCheckOut.setDate(newCheckOut.getDate() + duration);

      record.model.checkIn = formatDateQuery(targetDate as Date);
      record.model.checkOut = formatDateQuery(newCheckOut);

      await record.model.save();
    }

    this.calendarData.setupFasterLookup();
  }
}

declare module '@ember/service' {
  interface Registry {
    'calendar/interaction': CalendarInteractionService;
  }
}
